import { z } from 'zod';

import { apiClient } from '@/lib/api-client';

/**
 * Type-safe wrappers around the Contract C conversations surface
 * (`hub/app/api/conversations.py`). One module per resource so the queryKey
 * factory in `./keys.ts` stays the single import point for invalidation logic.
 *
 * `ConversationOut` carries `last_message_preview` directly — the backend's
 * LATERAL subquery returns either the latest message's text body or an
 * ASCII placeholder (`[Image]`, `[Voice]`, `[File]`, …) for a media message
 * without a caption. The active claim lock is still NOT embedded; it is
 * fetched lazily per-row via its own `['conversations', 'lock', id]` query.
 * See `keys.ts` for the shape contract.
 */

// --- Schemas (mirror hub/app/api/schemas.py) -------------------------------

const contactOutSchema = z.object({
  id: z.string().uuid(),
  peer_id: z.string(),
  peer_kind: z.string(),
  display_name: z.string().nullable(),
  phone_e164: z.string().nullable(),
  note: z.string().nullable(),
});

export const conversationOutSchema = z.object({
  id: z.string().uuid(),
  account_id: z.string().uuid(),
  contact: contactOutSchema,
  status: z.string(),
  assigned_agent_id: z.string().uuid().nullable(),
  last_message_at: z.string().nullable(),
  last_message_preview: z.string().nullable(),
  unread_count: z.number().int().nonnegative(),
  // GLOBAL operator flags (shared desk — every console sees the same value),
  // written via `PATCH /conversations/{id}` (`patchConversation` below):
  //   - `pinned_at`: null = not pinned; ISO timestamp = pin time. The SERVER
  //     sorts `pinned_at DESC NULLS LAST` before recency — the client renders
  //     pages in server order and never re-sorts locally.
  //   - `marked_unread`: manual attention flag; cleared automatically by
  //     `POST .../read` (any agent reading clears it for the whole desk).
  //   - `tags`: business-state labels; the console vocabulary is `CUCI_TAG`
  //     and `SPECIAL_TAG` but the array is open for Phase-2 risk labels.
  pinned_at: z.string().nullable(),
  marked_unread: z.boolean(),
  tags: z.array(z.string()),
  created_at: z.string(),
});

export type Conversation = z.infer<typeof conversationOutSchema>;

/**
 * The FIRST of the console's two business tags: 「要cuci」 — the conversation
 * is waiting on cuci (withdrawal / cash-out) processing. Cuci feeds both its
 * own filter view (`view=cuci`, one of the three views alongside
 * `unread_pin` and `special` — see `ConversationView`) and zone 0 of the
 * "All" tab's default four-zone ordering. The backend stores an open tag
 * ARRAY (Phase-2 risk labels will add more values); the UI toggles exactly
 * this string, so it lives here as the single source of the literal — grep
 * for CUCI_TAG rather than retyping the value.
 */
export const CUCI_TAG = '要cuci';

/**
 * The second business tag: "Special" — an operator-curated attention label
 * with its own filter tab (`view=special`). Same open-tags-array storage as
 * `CUCI_TAG`; this literal mirrors the hub's `SPECIAL_TAG` constant and must
 * stay byte-identical to it — grep for SPECIAL_TAG rather than retyping the
 * value. Unlike cuci, Special has NO zone of its own in the "All" tab's
 * default ordering (cuci → pinned → unread → read is unchanged); the tag
 * only drives the `view=special` filter.
 */
export const SPECIAL_TAG = 'Special';

const pagedConversationsSchema = z.object({
  items: z.array(conversationOutSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
});

export type PagedConversations = z.infer<typeof pagedConversationsSchema>;

export const lockOutSchema = z.object({
  conversation_id: z.string().uuid(),
  locked_by_agent_id: z.string().uuid(),
  locked_by_display_name: z.string(),
  locked_at: z.string(),
  expires_at: z.string(),
});

export type Lock = z.infer<typeof lockOutSchema>;

/** Body of a 409 returned by `POST /claim`. Same fields as `LockOut` but all
 *  strings — backend uses `dict[str, str]` directly. */
export const lockConflictDetailSchema = z.object({
  conversation_id: z.string(),
  locked_by_agent_id: z.string(),
  locked_by_display_name: z.string(),
  locked_at: z.string(),
  expires_at: z.string(),
});

export type LockConflictDetail = z.infer<typeof lockConflictDetailSchema>;

// --- Request types ---------------------------------------------------------

/**
 * Flag-driven list view. Mirrors the Hub's `view=` query param:
 *
 *   - `'unread_pin'` — only rows that are pinned OR unread
 *     (`unread_count > 0 OR marked_unread`); classic ordering (pinned first,
 *     then recency).
 *   - `'cuci'` — only rows whose `tags` contain `CUCI_TAG`; classic ordering.
 *   - `'special'` — only rows whose `tags` contain `SPECIAL_TAG`; classic
 *     ordering. Special deliberately has NO zone in the "All" ordering.
 *
 * Omitted entirely for the "All" tab — no row filter, and the server's
 * DEFAULT ordering is the four-zone sort (cuci → pinned → unread → read).
 */
export type ConversationView = 'unread_pin' | 'cuci' | 'special';

/**
 * Hard cap the backend enforces on the `q=` search param (`max_length=64` in
 * `hub/app/api/conversations.py`; Contract C "Search" — anything longer is a
 * `422`). Every producer of a search string MUST clamp to this before it can
 * reach the wire: an over-length `q=` fails the whole list query, and the
 * list's error branch replaces the ENTIRE inbox with a retry screen that can
 * never succeed. The input clamps at the keystroke (`maxLength`) and
 * `useConversations` clamps the normalized value again as the wire-level
 * guard (a paste-into-devtools or future programmatic set must not 422).
 */
export const CONVERSATION_SEARCH_MAX_LENGTH = 64;

export interface ListConversationsParams {
  /** `null` requests the aggregate "所有账号" list — the backend's
   *  `account_id` filter is simply omitted and every account's conversations
   *  come back. A UUID scopes the call to that one account. */
  accountId: string | null;
  /** Flag-driven row filter (see `ConversationView`). Omitted = "All". */
  view?: ConversationView;
  /** Same-phone grouping mode. `'phone'` forwards the backend's `group=phone`
   *  ordering (rows sharing a number across accounts become adjacent);
   *  meaningful only in the aggregate view, so `useConversations` sends it
   *  ONLY when `accountId === null`. Omitted here → default recency ordering. */
  group?: 'phone';
  /** Search text, forwarded as the backend's `q=` query param — a
   *  case-insensitive contact substring (display name / phone / peer id, with
   *  digit normalization for phone-shaped queries; see Contract C "Search").
   *  Sent TRIMMED and only when non-empty: the server treats whitespace-only
   *  as absent, so omitting the param entirely keeps request logs clean and
   *  the cache key space small. */
  search?: string;
  limit?: number;
  offset?: number;
}

// --- API surface -----------------------------------------------------------

/**
 * `GET /api/v1/conversations` — paged, filtered list.
 *
 * `accountId` mirrors the sidebar's `selectedAccountId`: a UUID scopes the
 * call to one account via the backend's `account_id` query param; `null`
 * ("所有账号") omits the param entirely, and the Hub returns
 * conversations across every account (`account_id` is an optional filter in
 * `hub/app/api/conversations.py` — no backend change needed for this).
 * `view` is forwarded as the backend's `view=` query param (omitted for the
 * "All" tab — see `ConversationView`); `search` as the `q=` param (trimmed,
 * omitted when empty — see `ListConversationsParams`).
 */
export async function listConversations(
  params: ListConversationsParams,
): Promise<PagedConversations> {
  const { accountId, view, group, search, limit = 50, offset = 0 } = params;
  const query: Record<string, string | number> = {
    limit,
    offset,
  };
  if (accountId !== null) query.account_id = accountId;
  if (view !== undefined) query.view = view;
  if (group !== undefined) query.group = group;
  const q = search?.trim();
  if (q) query.q = q;

  const response = await apiClient.get('/conversations', { params: query });
  return pagedConversationsSchema.parse(response.data);
}

/**
 * `POST /api/v1/conversations/{id}/claim` — take or renew the claim lock.
 *
 * `force=true` is sent as a query param (matches the backend). On 409 the
 * caller receives an axios error whose `response.data.detail` is a
 * `LockConflictDetail`; the mutation hook is responsible for parsing it.
 */
export async function claimConversation(
  conversationId: string,
  options: { force?: boolean } = {},
): Promise<Lock> {
  const response = await apiClient.post(
    `/conversations/${conversationId}/claim`,
    null,
    { params: options.force ? { force: true } : undefined },
  );
  return lockOutSchema.parse(response.data);
}

/**
 * `POST /api/v1/conversations/{id}/release` — drop the caller's claim lock.
 *
 * Returns `{ status: 'released' }` on success (also idempotent — the
 * backend returns the same shape when no lock exists at all).
 */
export async function releaseConversation(
  conversationId: string,
): Promise<{ status: string }> {
  const response = await apiClient.post(
    `/conversations/${conversationId}/release`,
  );
  return z.object({ status: z.string() }).parse(response.data);
}

/**
 * `POST /api/v1/conversations/{id}/read` — clear `unread_count`.
 *
 * Returns the updated `ConversationOut` so the cache can be reconciled.
 */
export async function markConversationRead(
  conversationId: string,
): Promise<Conversation> {
  const response = await apiClient.post(
    `/conversations/${conversationId}/read`,
  );
  return conversationOutSchema.parse(response.data);
}

/**
 * Body of `PATCH /api/v1/conversations/{id}` — a partial flags update. At
 * least ONE field must be present (the backend 422s an empty patch).
 *
 * - `pinned: true` pins (server stamps `pinned_at = now()` only if not
 *   already pinned — idempotent); `false` unpins.
 * - `marked_unread` sets/clears the manual attention flag.
 * - `tags` REPLACES the whole array (no add/remove delta — the caller sends
 *   the full desired set; last write wins across the shared desk).
 */
export interface ConversationFlagsPatch {
  pinned?: boolean;
  marked_unread?: boolean;
  tags?: string[];
}

/**
 * `PATCH /api/v1/conversations/{id}` — update the conversation's GLOBAL
 * operator flags (pin / mark-unread / business tags).
 *
 * Returns the updated `ConversationOut` so the mutation hook
 * (`useConversationFlags`) can reconcile the cache with server truth. The
 * backend also fans out `conversation.updated` on an actual change, so other
 * consoles converge without polling.
 */
export async function patchConversation(
  conversationId: string,
  patch: ConversationFlagsPatch,
): Promise<Conversation> {
  const response = await apiClient.patch(
    `/conversations/${conversationId}`,
    patch,
  );
  return conversationOutSchema.parse(response.data);
}

/**
 * `POST /api/v1/conversations/{id}/assign` — set the assignee.
 *
 * Stub for M5-T4: the UI does not surface an assign menu yet (planned for a
 * later milestone). Exported so the API layer is complete and a future
 * `useAssignConversation` mutation can use it without another round-trip
 * through this file.
 */
export async function assignConversation(
  conversationId: string,
  agentId: string,
): Promise<Conversation> {
  const response = await apiClient.post(
    `/conversations/${conversationId}/assign`,
    { agent_id: agentId },
  );
  return conversationOutSchema.parse(response.data);
}
